import log from '@/lib/logger';
import { type AppDispatch, type RootState } from '@/store';
import { type UnknownAction } from '@reduxjs/toolkit';
import { getPayments } from 'services/kushki';
import type { PaymentTable } from 'types/swagger';

export interface FilterPaymentsType {
  timestamp_start: string;
  timestamp_end: string;
  user_group_id?: number;
  payment_type?: string;
}

enum PaymentsActionTypes {
  SET_PAYMENTS = 'payments/set_payments',
  SET_IS_FETCHING_PAYMENTS = 'payments/set_is_fetching_payments',
}

export const setPayments = (payments: PaymentTable[]) => ({
  type: PaymentsActionTypes.SET_PAYMENTS,
  payload: payments,
});

export const setIsFetchingPayments = (isFetching: boolean) => ({
  type: PaymentsActionTypes.SET_IS_FETCHING_PAYMENTS,
  payload: isFetching,
});

interface PaymentAction {
  type: PaymentsActionTypes;
  payload: any;
}

interface PaymentsState {
  payments: {
    all: PaymentTable[];
    byId: Record<number, PaymentTable>;
  };
  isFetchingPayments: boolean;
}

const INIT_STATE: PaymentsState = {
  payments: {
    all: [],
    byId: {},
  },
  isFetchingPayments: false,
};

export default function paymentsReducer(state = INIT_STATE, action: UnknownAction): PaymentsState {
  const { type, payload } = action;

  switch (type) {
    case PaymentsActionTypes.SET_PAYMENTS: {
      return {
        ...state,
        payments: {
          ...state.payments,
          all: payload as PaymentTable[],
          byId: Object.fromEntries(
            (payload as PaymentTable[]).map((payment) => [payment.id, payment])
          ),
        },
      };
    }

    case PaymentsActionTypes.SET_IS_FETCHING_PAYMENTS: {
      return {
        ...state,
        isFetchingPayments: payload as boolean,
      };
    }

    default: {
      return state;
    }
  }
}

export const fetchPayments = (filters?: FilterPaymentsType) => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingPayments(true));

  try {
    const response = await getPayments(filters);

    if (response.data) dispatch(setPayments(response.data.results));

    return response;
  } catch (error) {
    log.error(error);
  } finally {
    dispatch(setIsFetchingPayments(false));
  }
};

export const selectAllPayments = (state: RootState) => state.payments.payments.all;
export const selectPaymentById = (state: RootState) => state.payments.payments.byId;
export const selectIsFetchingPayments = (state: RootState) => state.payments.isFetchingPayments;
